<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { ColumnConfig } from "@types"
import { useConfigStore } from "@core/stores/config.store"
import { storeToRefs } from "pinia"

// ================================
// SERVICES & STORES
// ================================

const configStore = useConfigStore()

const { isDefaultScope } = storeToRefs(configStore)

// ================================
// TYPES & INTERFACES
// ================================

type RoleCell = { value: boolean; text?: string }

type RoleRow = {
    name: string
    roles: {
        owner: RoleCell
        admin: RoleCell
        employee: RoleCell
        employee_read_only: RoleCell
    }
}

type TableRow = {
    id: string
    name: string
    owner: RoleCell
    admin: RoleCell
    employee: RoleCell
    employee_read_only: RoleCell
}

// ================================
// PROPS & EMITS
// ================================

const [visible] = defineModel<boolean>("visible", {
    required: true,
    default: false,
})

// ================================
// INTERNATIONALIZATION
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Rôles et permissions",
            subtitle:
                "Voici le détail des droits associés à chaque rôle dans votre espace entreprise.",
            faq: "Pour en savoir plus, consultez notre <a href='/faq' target='_blank' rel='noopener'>FAQ</a>.",
            warning:
                "Seul le Super Admin peut gérer les factures et souscriptions.",
            close: "Fermer",
            column: {
                feature: "Fonctionnalités",
                superAdmin: "Super Admin",
                admin: "Administrateur",
                member: "Membre",
                user: "Utilisateur",
            },
        },
    },
})

// ================================
// DATA – Rôles et permissions
// ================================

/**
 * Données des lignes du tableau (droits par rôle)
 */
const roleRows: RoleRow[] = [
    {
        name: "Accès aux factures et informations de facturation",
        roles: {
            owner: { value: true, text: " (exclusif)" },
            admin: { value: false },
            employee: { value: false },
            employee_read_only: { value: false },
        },
    },
    {
        name: "Souscription et modification des offres",
        roles: {
            owner: { value: true, text: " (exclusif)" },
            admin: { value: false },
            employee: { value: false },
            employee_read_only: { value: false },
        },
    },
    {
        name: "Création et attribution de rôles",
        roles: {
            owner: { value: true },
            admin: { value: true },
            employee: {
                value: true,
                text: " (peut créer des invités uniquement)",
            },
            employee_read_only: { value: false },
        },
    },
    {
        name: "Création et suppression de bâtiments et de sources",
        roles: {
            owner: { value: true },
            admin: { value: true },
            employee: { value: true, text: " (peut créer mais pas supprimer)" },
            employee_read_only: { value: false },
        },
    },
    {
        name: "Déplacement de capteurs entre bâtiments",
        roles: {
            owner: { value: true },
            admin: { value: true },
            employee: { value: true },
            employee_read_only: { value: false },
        },
    },
    {
        name: "Accès aux analyses métiers",
        roles: {
            owner: { value: true },
            admin: { value: true },
            employee: { value: true },
            employee_read_only: { value: false },
        },
    },
    {
        name: "Export des données",
        roles: {
            owner: { value: true },
            admin: { value: true },
            employee: { value: true },
            employee_read_only: { value: false },
        },
    },
    {
        name: "Soumission des demandes de consentement",
        roles: {
            owner: { value: true },
            admin: { value: true },
            employee: { value: true },
            employee_read_only: { value: false },
        },
    },
    {
        name: "Révocation des consentements",
        roles: {
            owner: { value: true },
            admin: { value: true },
            employee: { value: true },
            employee_read_only: { value: false },
        },
    },
    {
        name: "Accès aux données des bâtiments",
        roles: {
            owner: { value: true },
            admin: { value: true },
            employee: {
                value: true,
                text: " (uniquement les bâtiments qui lui sont partagés)",
            },
            employee_read_only: {
                value: true,
                text: " (uniquement les bâtiments qui lui sont partagés)",
            },
        },
    },
    {
        name: "Accès aux données des sources",
        roles: {
            owner: { value: true },
            admin: { value: true },
            employee: {
                value: true,
                text: " (uniquement les sources rattachées aux bâtiments qui lui sont partagés)",
            },
            employee_read_only: {
                value: true,
                text: " (uniquement les sources rattachées aux bâtiments qui lui sont partagés)",
            },
        },
    },
]

// ================================
// COMPUTED
// ================================

/**
 * Colonnes du tableau des rôles
 */
const getColumns = computed<ColumnConfig[]>(() => [
    {
        field: "name",
        header: t("column.feature"),
        sortable: false,
        style: { width: "250px", minWidth: "200px" },
    },
    {
        field: "owner",
        header: t("column.superAdmin"),
        sortable: false,
        style: { width: "150px" },
    },
    {
        field: "admin",
        header: t("column.admin"),
        sortable: false,
        style: { width: "150px" },
    },
    {
        field: "employee",
        header: t("column.member"),
        sortable: false,
        style: { width: "180px" },
    },
    {
        field: "employee_read_only",
        header: t("column.user"),
        sortable: false,
        style: { width: "150px" },
    },
])

/**
 * Lignes du tableau (format attendu par BaseTable)
 */
const getTableRows = computed<TableRow[]>(() =>
    roleRows.map((row, index) => ({
        id: `role-row-${index}-${row.name.slice(0, 20).replace(/\s/g, "-")}`,
        name: row.name,
        owner: row.roles.owner,
        admin: row.roles.admin,
        employee: row.roles.employee,
        employee_read_only: row.roles.employee_read_only,
    }))
)
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        modal
        :closable="true"
        dismissable-mask
        :header="t('title')"
        :style="{ width: '75vw' }"
        :breakpoints="{ '575px': '90vw' }"
        class="company-role-information-dialog"
    >
        <div
            class="company-role-information-dialog__content flex flex-col gap-4"
        >
            <div class="flex flex-col gap-2">
                <h4 class="text-sm font-medium text-surface-600">
                    {{ t("subtitle") }}
                </h4>
                <p
                    class="text-sm text-surface-600"
                    v-if="isDefaultScope"
                    v-html="t('faq')"
                />
            </div>
            <BaseTable
                data-key="id"
                :columns="getColumns"
                :value="getTableRows"
                size="small"
                class="company-role-information-dialog__table"
                cell-class="justify-center"
            >
                <template #cell_name="{ data }">
                    <p class="text-sm text-surface-700 text-center">
                        {{ data.name }}
                    </p>
                </template>
                <template #cell_owner="{ data }">
                    <div class="flex flex-col items-center gap-1 text-center">
                        <span v-if="data.owner?.value">✅</span>
                        <span v-else>❌</span>
                        <p
                            v-if="data.owner?.text"
                            class="text-xs text-surface-600"
                        >
                            {{ data.owner.text }}
                        </p>
                    </div>
                </template>
                <template #cell_admin="{ data }">
                    <div class="flex flex-col items-center gap-1 text-center">
                        <span v-if="data.admin?.value">✅</span>
                        <span v-else>❌</span>
                        <p
                            v-if="data.admin?.text"
                            class="text-xs text-surface-600"
                        >
                            {{ data.admin.text }}
                        </p>
                    </div>
                </template>
                <template #cell_employee="{ data }">
                    <div class="flex flex-col items-center gap-1 text-center">
                        <span v-if="data.employee?.value">✅</span>
                        <span v-else>❌</span>
                        <p
                            v-if="data.employee?.text"
                            class="text-xs text-surface-600"
                        >
                            {{ data.employee.text }}
                        </p>
                    </div>
                </template>
                <template #cell_employee_read_only="{ data }">
                    <div class="flex flex-col items-center gap-1 text-center">
                        <span v-if="data.employee_read_only?.value">✅</span>
                        <span v-else>❌</span>
                        <p
                            v-if="data.employee_read_only?.text"
                            class="text-xs text-surface-600"
                        >
                            {{ data.employee_read_only.text }}
                        </p>
                    </div>
                </template>
            </BaseTable>
            <p class="text-sm text-center text-surface-600">
                {{ t("warning") }}
            </p>
        </div>
    </BaseDialog>
</template>

<style scoped>
.company-role-information-dialog__table :deep(.base-table__header),
.company-role-information-dialog__table :deep(.base-table-row):nth-child(even) {
    background: var(--p-surface-100, #f6f9fc);
}

.company-role-information-dialog__table :deep(.base-table-row) {
    align-items: center;
}
</style>
